/**
 * The investment list (Epic 8).
 *
 * An investment entry is the counterpart to a Release verdict: "this object is
 * inadequate and the need is real". Keeping the two apart matters, because
 * deciding to let a thing go and deciding to replace it are different decisions
 * and conflating them is how a declutter turns into a shopping list.
 *
 * Entries are merged like everything else, so an entry can be acquired on one
 * device and abandoned on another. The status below resolves that by clock
 * rather than by precedence, so both devices reach the same answer.
 */
import { decode } from './hlc.js';
import { daysBetween, today } from './review.js';
const cell = (record, key) => record.fields.get(key);
const str = (record, key) => {
    const value = cell(record, key)?.v;
    return typeof value === 'string' && value !== '' ? value : null;
};
const asMoney = (value) => {
    if (value === null || typeof value !== 'object')
        return null;
    const candidate = value;
    return typeof candidate.minor === 'number' && typeof candidate.currency === 'string'
        ? { minor: candidate.minor, currency: candidate.currency }
        : null;
};
/**
 * The reference price, under either of the names it has had.
 *
 * `targetPrice` was renamed to `referencePrice` on 2026-10-09 (FR-8.1). Events
 * carrying the old key are in the log permanently, because the log is
 * append-only and FR-9.10 replays it -- so a past-date reconstruction produces
 * the old cell and nothing else, forever.
 *
 * The two are resolved by clock rather than by preferring the new name. Only
 * clock order is correct: a device still running the previous build can push a
 * `targetPrice` write after a `referencePrice` one, and preferring the new key
 * would silently discard the later edit -- which is exactly the kind of loss
 * `FR-10.6` forbids.
 */
function referencePriceOf(record) {
    const current = cell(record, 'referencePrice');
    const previous = cell(record, 'targetPrice');
    if (current === undefined)
        return asMoney(previous?.v);
    if (previous === undefined)
        return asMoney(current.v);
    return asMoney(current.hlc > previous.hlc ? current.v : previous.v);
}
const millisOf = (value) => {
    if (value === undefined)
        return null;
    try {
        return decode(value.hlc).millis;
    }
    catch {
        return null;
    }
};
/**
 * Whether an entry is still open, and if not, which way it closed.
 *
 * Decided by comparing the clocks of the two closing writes rather than by
 * preferring one event type. Two devices that disagree -- one records the thing
 * as bought, the other gives up on it -- must still reach the same conclusion,
 * and the later decision is the one that stands.
 */
export function investmentStatus(record) {
    const acquired = cell(record, 'acquiredItemId');
    const abandoned = cell(record, 'abandonedOn');
    if (acquired === undefined && abandoned === undefined)
        return 'open';
    if (abandoned === undefined)
        return 'acquired';
    if (acquired === undefined)
        return 'abandoned';
    return acquired.hlc > abandoned.hlc ? 'acquired' : 'abandoned';
}
function toEntry(investmentId, record, asOf) {
    const status = investmentStatus(record);
    const createdAt = millisOf(cell(record, 'name'));
    const closedOn = status === 'acquired' ? str(record, 'acquiredOn') : str(record, 'abandonedOn');
    return {
        investmentId,
        name: str(record, 'name') ?? '(unnamed)',
        notes: str(record, 'notes'),
        categoryId: str(record, 'categoryId'),
        referencePrice: referencePriceOf(record),
        priority: typeof cell(record, 'priority')?.v === 'number' ? cell(record, 'priority').v : 0,
        status,
        acquiredItemId: str(record, 'acquiredItemId'),
        acquiredOn: str(record, 'acquiredOn'),
        abandonedOn: str(record, 'abandonedOn'),
        abandonReason: status === 'abandoned' ? str(record, 'reason') : null,
        replaces: [...record.links.entries()]
            .filter(([, link]) => link.present)
            .map(([itemId]) => itemId),
        daysOpen: createdAt === null
            ? null
            : daysBetween(new Date(createdAt).toISOString().slice(0, 10), closedOn ?? asOf),
    };
}
/**
 * Every entry that has not been deleted.
 *
 * Open entries first and most-wanted at the top, because the list is read to
 * decide what to do next rather than to audit what has happened.
 */
export function listInvestments(manifest, asOf = today()) {
    const entries = [];
    for (const [investmentId, record] of manifest.investments) {
        if (record.lifecycle?.present === false)
            continue;
        entries.push(toEntry(investmentId, record, asOf));
    }
    const order = { open: 0, acquired: 1, abandoned: 2 };
    return entries.sort((a, b) => order[a.status] - order[b.status] || b.priority - a.priority || a.name.localeCompare(b.name));
}
export const openInvestments = (manifest, asOf = today()) => listInvestments(manifest, asOf).filter((entry) => entry.status === 'open');
/**
 * The items an entry would replace, as records.
 *
 * Links to items that no longer exist are dropped rather than rendered as a
 * blank row: an item can be deleted on another device while the entry survives,
 * and the link is a reference rather than a promise.
 */
export function replacedItems(manifest, entry) {
    return entry.replaces.flatMap((itemId) => {
        const item = manifest.items.get(itemId);
        return item === undefined ? [] : [item];
    });
}
/**
 * Open entries whose reference prices total, by currency.
 *
 * A reference price, not a target: the figure records roughly what the thing
 * costs, so the list can be read at a glance. Calling it a target would make the
 * list a commitment to spend, which is the opposite of what Epic 8 is for.
 */
export function plannedSpend(manifest, asOf = today()) {
    const totals = new Map();
    for (const entry of openInvestments(manifest, asOf)) {
        if (entry.referencePrice === null)
            continue;
        totals.set(entry.referencePrice.currency, (totals.get(entry.referencePrice.currency) ?? 0) + entry.referencePrice.minor);
    }
    return [...totals.entries()]
        .map(([currency, minor]) => ({ currency, minor }))
        .sort((a, b) => a.currency.localeCompare(b.currency));
}
//# sourceMappingURL=investments.js.map